import { useMutation, useQuery } from '@tanstack/react-query';
import {
  PushNotificationConfiguration,
  PushNotification,
  SendPushdNotificationBody,
  AppPushNotificationConfiguration,
} from '../types/notification';
import {
  createPushNotificationConfiguration,
  deleteNotification,
  getPushNotificationConfiguration,
  getScheduledNotifications,
  scheduleNotification,
  sendNotification,
  updatePushNotificationConfiguration,
} from '../api/push-notification';
import { useUserAuth } from '../contexts/auth';
import { Optional } from 'utility-types';

const NOTIFICATION_API = process.env.REACT_APP_PUSH_NOTIFICATIONS;

export interface MatchFormData {
  matchId: number;
  matchName: string;
  matchDate: string;
  leagueName: string;
  season: number;
}

export interface PollFormData {
  pollId: string;
  pollName: string;
}

export interface PushNotificationFormData {
  matchFormData: MatchFormData[];
  pollFormData: PollFormData[];
}

const getFormData = async (abbrv: string, auth?: string): Promise<PushNotificationFormData> => {
  const response = await fetch(`${NOTIFICATION_API}/notifications/form-data/${abbrv}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: auth ? `Bearer ${auth}` : '',
    },
  });
  const data = (await response.json()) as PushNotificationFormData;
  return data || null;
};

export function usePushNotificationFormData(abbrv: string) {
  const { currentUser } = useUserAuth();

  const query = useQuery({
    queryKey: ['push-notification', 'formdata', abbrv],
    queryFn: async () => {
      if (!abbrv) {
        return null;
      }
      const formData = await getFormData(abbrv, await currentUser?.getIdToken());
      return formData;
    },
    refetchOnWindowFocus: false,
  });
  return {
    query,
  };
}

export function usePushNotificationSend(abbrv: string) {
  const { currentUser } = useUserAuth();

  return useMutation({
    mutationFn: async (payload: {
      body: SendPushdNotificationBody;
      collapseKey?: string;
      auth?: string;
    }) => {
      const response = await sendNotification(
        abbrv,
        payload.body,
        payload.collapseKey,
        await currentUser?.getIdToken()
      );
      return response;
    },
  });
}

export function usePushNotificationSchedule(abbrv: string) {
  const { currentUser } = useUserAuth();

  return useMutation({
    mutationFn: async (payload: { body: PushNotification; scheduledTime: string; app: string }) => {
      const auth = await currentUser?.getIdToken();
      const response = await scheduleNotification({
        abbrv,
        body: {
          notification: payload.body,
          scheduledTime: payload.scheduledTime,
          app: abbrv,
        },
        auth,
      });
      return response;
    },
  });
}

export function usePushNotificationDelete() {
  const { currentUser } = useUserAuth();

  return useMutation({
    mutationFn: async (params: { id: string }) => {
      const { id } = params;
      const auth = await currentUser?.getIdToken();
      if (!auth) {
        return null;
      }

      await deleteNotification(id, auth);
    },
  });
}

export function usePushNotificationConfiguration(abbrv: string) {
  const { currentUser } = useUserAuth();
  const query = useQuery({
    queryKey: ['push-notification', 'configuration', abbrv],
    queryFn: async () => {
      const auth = await currentUser?.getIdToken();
      if (!abbrv || !auth) {
        return null;
      }
      const configuration = await getPushNotificationConfiguration(abbrv, auth);
      return configuration;
    },
    refetchOnWindowFocus: false,
  });
  return query;
}

export function usePushNotificationConfigurationUpdate(abbrv: string) {
  const { currentUser } = useUserAuth();
  return useMutation({
    mutationFn: async (payload: {
      newsConfiguration?: Optional<
        PushNotificationConfiguration,
        'autoPush' | 'defaultTitle' | 'defaultDescription'
      >;
    }) => {
      const auth = await currentUser?.getIdToken();
      if (!auth) {
        throw new Error();
      }
      const app = abbrv;
      const configuration = await getPushNotificationConfiguration(abbrv, auth);

      const saveResponse = await (async () => {
        if (!configuration) {
          const response = await createPushNotificationConfiguration(
            {
              app,
              ...payload,
            } as AppPushNotificationConfiguration,
            auth
          );
          return response;
        }
        const updateData = { ...payload };
        const configurationTypes: Array<keyof typeof payload> = ['newsConfiguration'];
        configurationTypes.forEach((key) => {
          if (updateData[key]) {
            updateData[key] = {
              ...configuration.newsConfiguration,
              ...updateData.newsConfiguration,
            };
          }
        });

        const response = await updatePushNotificationConfiguration(
          {
            app,
            ...updateData,
          } as AppPushNotificationConfiguration,
          auth
        );

        return response;
      })();

      if (saveResponse.error) {
        throw new Error(saveResponse.message);
      }

      if (configuration.newsConfiguration) {
        // TODO: update all news
      }
      return saveResponse;
    },
  });
}

export function usePushNotifications(abbrv?: string | null) {
  const { currentUser } = useUserAuth();
  const query = useQuery({
    queryKey: ['push-notifications', 'list', abbrv],
    queryFn: async () => {
      const auth = await currentUser?.getIdToken();
      if (!abbrv || !auth) {
        return null;
      }
      const notifications = await getScheduledNotifications(abbrv, auth);
      return notifications;
    },
    refetchOnWindowFocus: false,
  });

  return query;
}
